import { t as uiText } from '../../i18n';
/**
 * Offer Entitlement のフロント互換層。
 * 正本は functions/src/entitlements/ 。型・人数/容量の数値をこちらと同期すること。
 *
 * child はプランではない。parentWorkspaceId があるときは親の Offer を渡す。
 */

export const FEATURE_KEYS = [
  // --- 既存 13 キー ---
  'members',
  'storage',
  'aiSearch',
  'publicRag',
  'crawler',
  'fileIngest',
  'widget',
  'oga',
  'drs',
  'analytics',
  'multilingual',
  'voiceInput',
  'answerFilter',

  // --- 共通基盤 ---
  'company',
  'workspace.linkage',
  'workspace.icon',
  'workspace.baseColor',
  'notification.page',
  'notification.thread',
  'notification.slack',

  // --- 公開 RAG 配下 ---
  'publicRag.webPages',
  'publicRag.ogMeta',
  'publicRag.tabAppearance',
  'publicRag.font',
  'publicRag.header',
  'publicRag.header.logo',
  'publicRag.header.links',
  'publicRag.designColor',
  'publicRag.titleIcon',
  'publicRag.contentText',
  'publicRag.privacyNotice',
  'publicRag.privacyNotice.consent',
  'publicRag.textSizeControl',
  'publicRag.sessionHistory',
  'publicRag.footer',

  // --- ウィジェット配下 ---
  'widget.buttonText',
  'widget.buttonImage',
  'widget.icon',
  'widget.closeButton',
  'widget.tooltip',
  'widget.position',
  'widget.mobileCallButton',
  'widget.apiToken',
  'widget.cacheMode',

  // --- クローラー配下 ---
  'crawler.bulkRegister',
  'crawler.urlList',
  'crawler.schedule',

  // --- 回答フィルター配下 ---
  'answerFilter.buttons',
  'answerFilter.urlPrefix',
  'answerFilter.hashtag',

  // --- OGA 配下 ---
  'oga.answerPage',
  'oga.inlineSourceUrls',
  'oga.crawlReferencedUrls',
  'oga.contactHeading',
  'oga.contactUrl',
  'oga.contactPhone',
  'oga.customLink',

  // --- DRS 配下 ---
  'drs.aiGenerate',
  'drs.manualEdit',
  'drs.multilingual',
  'drs.suggest',
  'drs.keyword',

  // --- AIクレジット（利用枠。Operation prefix はなし） ---
  'credits.agent',
  'credits.internalRag',
  'credits.publicRag',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export type PlanId = 'free' | 'starter' | 'standard' | 'team' | 'exclusive';

export type SeriesId = 'NCN' | 'NCG';

export type OfferId =
  | 'ncn:free'
  | 'ncn:starter'
  | 'ncn:standard'
  | 'ncn:team'
  | 'ncn:exclusive'
  | 'ncg:free'
  | 'ncg:starter'
  | 'ncg:standard'
  | 'ncg:team'
  | 'ncg:exclusive';

export type LimitValue = number | null;

export type FeatureEntitlement = {
  enabled: boolean;
  limits?: Record<string, LimitValue>;
};

export type FeatureEntitlements = Record<FeatureKey, FeatureEntitlement>;

export type EffectiveEntitlements = {
  offerId: OfferId;
  planId: PlanId;
  seriesId: SeriesId;
  features: FeatureEntitlements;
};

export type WorkspaceEntitlementOverride = Partial<{
  [K in FeatureKey]: Partial<FeatureEntitlement>;
}>;

export type WorkspaceOfferFields = {
  seriesId?: unknown;
  planId?: unknown;
  offerId?: unknown;
};

export type WorkspaceEntitlementInput = WorkspaceOfferFields & {
  parentWorkspaceId?: unknown;
  parent?: WorkspaceOfferFields;
  override?: WorkspaceEntitlementOverride;
};

export interface Entitlements {
  memberLimit: LimitValue;
  storageLimitBytes: LimitValue;
}

const GB = 1024 * 1024 * 1024;
const TB = 1024 * 1024 * 1024 * 1024;

const PLAN_MEMBER_STORAGE: Record<PlanId, Entitlements> = {
  free: { memberLimit: 3, storageLimitBytes: 1 * GB },
  starter: { memberLimit: 10, storageLimitBytes: 10 * GB },
  standard: { memberLimit: 30, storageLimitBytes: 100 * GB },
  team: { memberLimit: 100, storageLimitBytes: 500 * GB },
  exclusive: { memberLimit: null, storageLimitBytes: 1 * TB },
};

/** 毎回新しいオブジェクトを返す（OFFER_ENTITLEMENTS 間で参照を共有しない） */
const on = (): FeatureEntitlement => ({ enabled: true });
const off = (): FeatureEntitlement => ({ enabled: false });

/** 下位プランほど先。atLeast の比較に使う */
const PLAN_ORDER: readonly PlanId[] = ['free', 'starter', 'standard', 'team', 'exclusive'];

const atLeast = (planId: PlanId, min: PlanId): FeatureEntitlement =>
  PLAN_ORDER.indexOf(planId) >= PLAN_ORDER.indexOf(min) ? on() : off();

/** standard 以上で提供 */
const standardUp = (planId: PlanId): FeatureEntitlement => atLeast(planId, 'standard');
/** team 以上で提供 */
const teamUp = (planId: PlanId): FeatureEntitlement => atLeast(planId, 'team');

/**
 * 公開 AI: free 80 / starter 8000 / standard 以上 16000。
 * free の初回 WS には別途ウェルカムボーナス（creditGrants/welcomeBonus）が付く。
 */
function creditMonthly(planId: PlanId): { agent: number; internalRag: number; publicRag: number } {
  if (planId === 'free') {
    return { agent: 0, internalRag: 1200, publicRag: 80 };
  }
  if (planId === 'starter') {
    return { agent: 0, internalRag: 8000, publicRag: 8000 };
  }
  return { agent: 0, internalRag: 8000, publicRag: 16000 };
}

/**
 * NCN（ノートシリーズ）のデフォルト。
 * 公開 RAG 系（publicRag / widget / crawler / answerFilter / oga / drs とその配下）、
 * 多言語、音声入力、ファイル取込は NCG 専用のため disabled。
 * functions/src/entitlements/defaults.ts の ncnFeaturesForPlan と同期すること。
 */
function ncnFeaturesForPlan(planId: PlanId): FeatureEntitlements {
  const limits = PLAN_MEMBER_STORAGE[planId];
  return {
    // --- 共通基盤 ---
    members:      { enabled: true,  limits: { limit: limits.memberLimit } },
    storage:      { enabled: true,  limits: { limitBytes: limits.storageLimitBytes } },
    // aiSearch / analytics は常時 ON（ON/OFF の対象にしない。featureHierarchy.ts の ALWAYS_ON_FEATURE_KEYS 参照）
    aiSearch:     on(),
    analytics:    on(),
    company:      teamUp(planId),
    'workspace.linkage':   standardUp(planId),
    'workspace.icon':      on(),
    'workspace.baseColor': on(),
    'notification.page':   standardUp(planId),
    'notification.thread': standardUp(planId),
    'notification.slack':  standardUp(planId),

    // --- 公開 RAG（NCG 専用） ---
    publicRag:                         off(),
    'publicRag.webPages':              off(),
    'publicRag.ogMeta':                off(),
    'publicRag.tabAppearance':         off(),
    'publicRag.font':                  off(),
    'publicRag.header':                off(),
    'publicRag.header.logo':           off(),
    'publicRag.header.links':          off(),
    'publicRag.designColor':           off(),
    'publicRag.titleIcon':             off(),
    'publicRag.contentText':           off(),
    'publicRag.privacyNotice':         off(),
    'publicRag.privacyNotice.consent': off(),
    'publicRag.textSizeControl':       off(),
    'publicRag.sessionHistory':        off(),
    'publicRag.footer':                off(),
    multilingual:                      off(),
    voiceInput:                        off(),

    // --- ウィジェット（NCG 専用） ---
    widget:               off(),
    'widget.buttonText':  off(),
    'widget.buttonImage': off(),
    'widget.icon':        off(),
    'widget.closeButton': off(),
    'widget.tooltip':     off(),
    'widget.position':    off(),
    'widget.mobileCallButton': off(),
    'widget.apiToken':    off(),
    'widget.cacheMode':   off(),

    // --- クローラー（NCG 専用） ---
    crawler:                off(),
    'crawler.bulkRegister': off(),
    'crawler.urlList':      off(),
    'crawler.schedule':     off(),
    fileIngest:             off(),

    // --- 回答フィルター（NCG 専用） ---
    answerFilter:             off(),
    'answerFilter.buttons':   off(),
    'answerFilter.urlPrefix': off(),
    'answerFilter.hashtag':   off(),

    // --- OGA（NCG 専用） ---
    oga:                       off(),
    'oga.answerPage':          off(),
    'oga.inlineSourceUrls':    off(),
    'oga.crawlReferencedUrls': off(),
    'oga.contactHeading':      off(),
    'oga.contactUrl':          off(),
    'oga.contactPhone':        off(),
    'oga.customLink':          off(),

    // --- DRS（NCG 専用） ---
    drs:                off(),
    'drs.aiGenerate':   off(),
    'drs.manualEdit':   off(),
    'drs.multilingual': off(),
    'drs.suggest':      off(),
    'drs.keyword':      off(),

    // --- AIクレジット ---
    'credits.agent': {
      enabled: true,
      limits: { monthlyCredits: creditMonthly(planId).agent },
    },
    'credits.internalRag': {
      enabled: true,
      limits: { monthlyCredits: creditMonthly(planId).internalRag },
    },
    'credits.publicRag': { enabled: false, limits: { monthlyCredits: 0 } },
  };
}

/**
 * NCG（公開 RAG シリーズ）のデフォルト。プラン差の大半は standard 以上か否かで決まる。
 * functions/src/entitlements/defaults.ts の ncgFeaturesForPlan と同期すること。
 */
function ncgFeaturesForPlan(planId: PlanId): FeatureEntitlements {
  const limits = PLAN_MEMBER_STORAGE[planId];
  return {
    // --- 共通基盤 ---
    members:      { enabled: true,  limits: { limit: limits.memberLimit } },
    storage:      { enabled: true,  limits: { limitBytes: limits.storageLimitBytes } },
    // aiSearch / analytics は常時 ON（ON/OFF の対象にしない。featureHierarchy.ts の ALWAYS_ON_FEATURE_KEYS 参照）
    aiSearch:     on(),
    analytics:    on(),
    company:      teamUp(planId),
    'workspace.linkage':   standardUp(planId),
    'workspace.icon':      on(),
    'workspace.baseColor': on(),
    'notification.page':   standardUp(planId),
    'notification.thread': standardUp(planId),
    'notification.slack':  standardUp(planId),

    // --- 公開 RAG ---
    publicRag:                         on(),
    'publicRag.webPages':              standardUp(planId),
    'publicRag.ogMeta':                standardUp(planId),
    'publicRag.tabAppearance':         standardUp(planId),
    'publicRag.font':                  standardUp(planId),
    'publicRag.header':                standardUp(planId),
    'publicRag.header.logo':           standardUp(planId),
    'publicRag.header.links':          standardUp(planId),
    'publicRag.designColor':           standardUp(planId),
    'publicRag.titleIcon':             standardUp(planId),
    'publicRag.contentText':           on(),
    'publicRag.privacyNotice':         standardUp(planId),
    'publicRag.privacyNotice.consent': standardUp(planId),
    'publicRag.textSizeControl':       standardUp(planId),
    'publicRag.sessionHistory':        standardUp(planId),
    'publicRag.footer':                standardUp(planId),
    multilingual:                      on(),
    voiceInput:                        standardUp(planId),

    // --- ウィジェット ---
    widget:               on(),
    'widget.buttonText':  on(),
    'widget.buttonImage': standardUp(planId),
    'widget.icon':        standardUp(planId),
    'widget.closeButton': on(),
    'widget.tooltip':     standardUp(planId),
    'widget.position':    on(),
    'widget.mobileCallButton': standardUp(planId),
    'widget.apiToken':    on(),
    'widget.cacheMode':   standardUp(planId),

    // --- クローラー ---
    crawler:                on(),
    'crawler.bulkRegister': on(),
    'crawler.urlList':      on(),
    'crawler.schedule':     on(),
    fileIngest:             on(),

    // --- 回答フィルター ---
    answerFilter:             standardUp(planId),
    'answerFilter.buttons':   standardUp(planId),
    'answerFilter.urlPrefix': standardUp(planId),
    'answerFilter.hashtag':   standardUp(planId),

    // --- OGA ---
    oga:                       on(),
    'oga.answerPage':          standardUp(planId),
    'oga.inlineSourceUrls':    on(),
    'oga.crawlReferencedUrls': standardUp(planId),
    'oga.contactHeading':      standardUp(planId),
    'oga.contactUrl':          on(),
    'oga.contactPhone':        on(),
    'oga.customLink':          standardUp(planId),

    // --- DRS ---
    drs:                standardUp(planId),
    'drs.aiGenerate':   standardUp(planId),
    'drs.manualEdit':   standardUp(planId),
    'drs.multilingual': standardUp(planId),
    'drs.suggest':      standardUp(planId),
    'drs.keyword':      standardUp(planId),

    // --- AIクレジット ---
    'credits.agent': {
      enabled: true,
      limits: { monthlyCredits: creditMonthly(planId).agent },
    },
    'credits.internalRag': {
      enabled: true,
      limits: { monthlyCredits: creditMonthly(planId).internalRag },
    },
    'credits.publicRag': {
      enabled: true,
      limits: { monthlyCredits: creditMonthly(planId).publicRag },
    },
  };
}

const OFFER_ENTITLEMENTS: Record<OfferId, FeatureEntitlements> = {
  'ncn:free':       ncnFeaturesForPlan('free'),
  'ncn:starter':    ncnFeaturesForPlan('starter'),
  'ncn:standard':   ncnFeaturesForPlan('standard'),
  'ncn:team':       ncnFeaturesForPlan('team'),
  'ncn:exclusive':  ncnFeaturesForPlan('exclusive'),
  'ncg:free':       ncgFeaturesForPlan('free'),
  'ncg:starter':    ncgFeaturesForPlan('starter'),
  'ncg:standard':   ncgFeaturesForPlan('standard'),
  'ncg:team':       ncgFeaturesForPlan('team'),
  'ncg:exclusive':  ncgFeaturesForPlan('exclusive'),
};

/** @deprecated Offer 単位で見る。互換のため残す。child は含めない。 */
export const ENTITLEMENTS_BY_PLAN_ID: Record<PlanId, Entitlements> = PLAN_MEMBER_STORAGE;

/** @deprecated resolveEffectiveEntitlements を使う。 */
export const ENTITLEMENTS_BY_OFFER_ID: Record<OfferId, Entitlements> = {
  'ncn:free': PLAN_MEMBER_STORAGE.free,
  'ncn:starter': PLAN_MEMBER_STORAGE.starter,
  'ncn:standard': PLAN_MEMBER_STORAGE.standard,
  'ncn:team': PLAN_MEMBER_STORAGE.team,
  'ncn:exclusive': PLAN_MEMBER_STORAGE.exclusive,
  'ncg:free': PLAN_MEMBER_STORAGE.free,
  'ncg:starter': PLAN_MEMBER_STORAGE.starter,
  'ncg:standard': PLAN_MEMBER_STORAGE.standard,
  'ncg:team': PLAN_MEMBER_STORAGE.team,
  'ncg:exclusive': PLAN_MEMBER_STORAGE.exclusive,
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isChildPlanId(planId: string): boolean {
  return planId.toLowerCase() === 'child';
}

function isChildOfferId(offerId: string): boolean {
  return /:child$/i.test(offerId);
}

export function normalizePlanId(input: unknown): PlanId {
  const v = typeof input === 'string' ? input.trim().toLowerCase() : '';
  switch (v) {
    case 'free':
    case 'starter':
    case 'standard':
    case 'team':
    case 'exclusive':
      return v;
    default:
      return 'free';
  }
}

function normalizeSeriesId(input: unknown, offerId?: string): SeriesId {
  const raw = asString(input).toUpperCase();
  if (raw === 'NCG' || raw === 'NCN') return raw;
  const offer = (offerId || '').toLowerCase();
  if (offer.startsWith('ncg:')) return 'NCG';
  return 'NCN';
}

function isKnownOfferId(input: string): input is OfferId {
  return Object.prototype.hasOwnProperty.call(OFFER_ENTITLEMENTS, input);
}

function toOfferId(seriesId: SeriesId, planId: PlanId): OfferId {
  return `${seriesId.toLowerCase()}:${planId}` as OfferId;
}

function cloneFeatures(features: FeatureEntitlements): FeatureEntitlements {
  return JSON.parse(JSON.stringify(features)) as FeatureEntitlements;
}

function applyOverride(
  features: FeatureEntitlements,
  override: WorkspaceEntitlementOverride | undefined
): void {
  if (!override) return;
  (Object.keys(override) as FeatureKey[]).forEach((key) => {
    const patch = override[key];
    if (!patch) return;
    const current = features[key];
    features[key] = {
      enabled: patch.enabled ?? current.enabled,
      limits: patch.limits ? { ...current.limits, ...patch.limits } : current.limits,
    };
  });
}

function pickOfferSource(input: WorkspaceEntitlementInput): WorkspaceOfferFields {
  const parentId = asString(input.parentWorkspaceId);
  const selfPlan = asString(input.planId);
  const selfOffer = asString(input.offerId);
  const selfIsChildPlaceholder = isChildPlanId(selfPlan) || isChildOfferId(selfOffer);
  if (parentId && input.parent) return input.parent;
  if (selfIsChildPlaceholder && input.parent) return input.parent;
  return input;
}

function resolveOffer(fields: WorkspaceOfferFields): {
  offerId: OfferId;
  planId: PlanId;
  seriesId: SeriesId;
} {
  const offerRaw = asString(fields.offerId).toLowerCase();
  const planRaw = asString(fields.planId);
  const series = normalizeSeriesId(fields.seriesId, offerRaw);

  if (isKnownOfferId(offerRaw) && !isChildOfferId(offerRaw)) {
    const planId = normalizePlanId(offerRaw.split(':')[1] || planRaw);
    return { offerId: offerRaw, planId, seriesId: series };
  }

  const planId = isChildPlanId(planRaw) ? 'free' : normalizePlanId(planRaw);
  return { offerId: toOfferId(series, planId), planId, seriesId: series };
}

export function resolveEffectiveEntitlements(
  input: WorkspaceEntitlementInput
): EffectiveEntitlements {
  const { offerId, planId, seriesId } = resolveOffer(pickOfferSource(input));
  const features = cloneFeatures(OFFER_ENTITLEMENTS[offerId]);
  applyOverride(features, input.override);
  return { offerId, planId, seriesId, features };
}

export function getEntitlements(params: WorkspaceEntitlementInput): Entitlements {
  const effective = resolveEffectiveEntitlements(params);
  return {
    memberLimit: effective.features.members.limits?.limit ?? null,
    storageLimitBytes: effective.features.storage.limits?.limitBytes ?? null,
  };
}

export function formatMemberLimit(limit: LimitValue): string {
  return limit === null ? uiText("Unlimited") : uiText("{{limit}} people", { limit: limit });
}
